import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  AlertTriangle,
  BrushCleaning,
  Eye,
  Loader2,
  Moon,
  RotateCcw,
  Search,
  Sun,
} from 'lucide-react';

import PrivateLayout from '../../layouts/PrivateLayout';
// theme-snap (an instant theme flip) lives here.
import '../onboardingSupport/onboardingSupport.css';
import { cn } from '../../../lib/utils';
import {
  fetchOnboardingTextDraft,
  useClientActivity,
  useClientOnboardingBoard,
  useClientOnboardingNurture,
  useClientOnboardingStream,
  useMarkActivitySeen,
  useSendOnboardingText,
} from '../../../services/clientOnboarding';
import OnboardingDesk, { clientsNeedingYou } from './OnboardingDesk';
import ScoreboardChip from '../workspace/ScoreboardChip';
import NudgeComposer from '../onboardingSupport/potentialClients/NudgeComposer';
import StalledView from './StalledView';
import SignedSasView from './SignedSasView';
import ClientWorkspace from './ClientWorkspace';
import ClientActivityButton from './ClientActivityButton';
import ClientsGuide from './ClientsGuide';
import ConnectionsControl from '../onboardingSupport/ConnectionsControl';
import TeamSettingsButton from '../onboardingSupport/TeamSettingsButton';
import EagleEyeButton from '../journeys/EagleEye';
import MeetingsCalendar from '../onboardingSupport/potentialClients/MeetingsCalendar';
import useMeetingAlerts from '../onboardingSupport/potentialClients/useMeetingAlerts';
import { useCallCenter } from '../onboardingSupport/callCenter/CallCenterProvider';
import { useUser } from '../../../contexts/UserContext';
import { isOwnedBy } from '../onboardingSupport/ownership';
import { SearchFilterBar } from '../workspace/DeskKit';
import {
  COLUMNS,
  SECTIONS,
  THEME_KEY,
} from './clientOnboardingUtils';

// Client Onboarding (Nic 2026-09-28): the Onboarding Specialist's page.
// Every signed client walks the checklist here until they are ready for a
// Claims Manager. Replaces the logic-free Case Readiness slide-out.

const readTheme = () => {
  try {
    return (
      localStorage.getItem(THEME_KEY) ||
      localStorage.getItem('osBoardTheme') ||
      'light'
    );
  } catch {
    return 'light';
  }
};

const CARD =
  'rounded-2xl bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04),0_2px_10px_rgba(15,23,42,0.06)] dark:!bg-surface-card dark:ring-1 dark:ring-white/10';

const ClientOnboardingPage = () => {
  const [theme, setTheme] = useState(readTheme);
  const [section, setSection] = useState('board');
  const [query, setQuery] = useState('');
  // ?open=<onboardingId> (the claim profile's checklist button) opens them.
  const [searchParams, setSearchParams] = useSearchParams();
  const [openId, setOpenId] = useState(() => searchParams.get('open'));
  // The call dock's expand points here with ?open= (Nic 2026-10-10): open
  // them, then drop the param so the next expand (same client) opens again
  // (QA 2026-10-13: the second one did nothing).
  const openParam = searchParams.get('open');
  useEffect(() => {
    if (!openParam) return;
    setOpenId(openParam);
    const next = new URLSearchParams(searchParams);
    next.delete('open');
    setSearchParams(next, { replace: true });
  }, [openParam, searchParams, setSearchParams]);
  // Where a client opens: a desk row's note icon opens them on Notes (Nic
  // 2026-10-12); a client activity line on the tab that shows the change.
  const [openAt, setOpenAt] = useState(null); // { id, tab }
  const openClientAt = (id, tab) => {
    setOpenAt(tab ? { id, tab } : null);
    setOpenId(id);
  };
  // A desk row's nudge icon (Nic 2026-10-27, like Sales): the composer with
  // their nudge wording, without opening them.
  const [nudge, setNudge] = useState(null); // { onboardingId, name }
  const sendText = useSendOnboardingText();
  const board = useClientOnboardingBoard();
  // Live (client activity, Nic 2026-10-23): any change to a client refreshes
  // the page at once. The 30 second poll stays as the fallback.
  useClientOnboardingStream(true);
  // Nurture pages on the server (2026-09-29). The search box drives it too
  // (debounced), so a search reaches every nurtured client, not one page.
  const [nurtureQ, setNurtureQ] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setNurtureQ(query.trim()), 250);
    return () => clearTimeout(t);
  }, [query]);
  // Only for the search's hit count on the Stalled tab.
  const nurture = useClientOnboardingNurture({ q: nurtureQ }, { enabled: !!nurtureQ });
  // Meetings (the Sales Rep tools for specialists, 2026-09-28): the calendar,
  // the 15 / 5 minute alerts, and a call dialog for any meeting or client.
  const [meetingsOpen, setMeetingsOpen] = useState(false);
  const [tick, setTick] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setTick(new Date()), 30000);
    return () => clearInterval(t);
  }, []);
  // Ownership rule (Nic 2026-09-29): Call now only on the viewer's own
  // clients. The board payload carries every owned card the viewer can see
  // (their own always), so a miss here means "not yours".
  const { user } = useUser();
  const ownerByOnboarding = useMemo(() => {
    const map = new Map();
    Object.values(board.data?.columns || {}).forEach((list) =>
      (list || []).forEach((c) => map.set(c.onboardingId, c.ownerUserId)),
    );
    return map;
  }, [board.data]);
  const meetingIsMine = (m) =>
    !!m?.onboardingId &&
    isOwnedBy(ownerByOnboarding.get(m.onboardingId), user?.userId);
  // Calling from a meeting reminder or the Meetings calendar (Nic 2026-10-10):
  // the app's call center, with their Workspace open to carry the call.
  const callCenter = useCallCenter();
  const callClient = (m) => {
    if (!m?.onboardingId || !m?.leadPhone) return;
    const card = Object.values(board.data?.columns || {})
      .flat()
      .find((c) => c.onboardingId === m.onboardingId);
    const name = card?.name || `${m.leadFirstName || ''} ${m.leadLastName || ''}`.trim();
    const [first, ...rest] = name.split(/\s+/);
    setMeetingsOpen(false);
    setOpenId(m.onboardingId);
    callCenter.startClientCall?.({
      onboardingId: m.onboardingId,
      firstName: first || '',
      lastName: rest.join(' '),
      phone: card?.phone || m.leadPhone,
    });
  };
  // The 15 / 10 / 5 minute meeting reminders (no Meetings button needed).
  useMeetingAlerts({
    enabled: true,
    now: tick,
    apiBase: '/client-onboarding',
    canAct: meetingIsMine,
    onOpen: (m) => m.onboardingId && setOpenId(m.onboardingId),
    onCall: (m) => callClient(m),
  });

  // Both themes (Nic's rule): dark lives on <html> while this page is up,
  // the same way the board does it, so portals (slide-over, composer) match.
  useLayoutEffect(() => {
    const root = document.documentElement;
    if (theme === 'dark') root.classList.add('dark');
    else root.classList.remove('dark');
    return () => root.classList.remove('dark');
  }, [theme]);
  // Toasts like the Sales board's (Nic 2026-10-10): bottom left, in the
  // page's theme. The rules in onboardingSupport.css hang off this class.
  useLayoutEffect(() => {
    document.documentElement.classList.add('os-board');
    return () => document.documentElement.classList.remove('os-board');
  }, []);
  // The flip is instant, like the Sales board's (Nic 2026-10-10): theme-snap
  // (onboardingSupport.css) turns every transition off for the flip frame,
  // so the page and the cards change together instead of in stages.
  const setThemeTo = (next) => {
    const root = document.documentElement;
    root.classList.add('theme-snap');
    requestAnimationFrame(() => requestAnimationFrame(() => root.classList.remove('theme-snap')));
    setTheme(next);
    try {
      localStorage.setItem(THEME_KEY, next);
    } catch {
      /* private mode: this session only */
    }
  };

  const data = board.data;
  const viewOnly = !!data?.viewOnly;
  // Managers, admins and view only always see everyone and filter by
  // specialist; a specialist sees their own (no Everyone toggle since Nic
  // 2026-10-11: it confused people)
  // (Nic 2026-10-05, like Sales).
  const seesAll = !!data?.seesAll;
  const showingAll = seesAll;
  const checklistLabels = useMemo(
    () => Object.fromEntries((data?.checklist || []).map((c) => [c.key, c.label])),
    [data],
  );
  // Opening a client clears what's new from them, for good (kept on the
  // server per person). Also while they're open: whatever they do next is
  // seen as it lands. Only when there is something new, so opening a quiet
  // client costs nothing.
  const activity = useClientActivity('mine');
  const markSeen = useMarkActivitySeen();
  const openCard = useMemo(
    () => (openId ? Object.values(data?.columns || {}).flat().find((c) => c.onboardingId === openId) : null),
    [data, openId],
  );
  const openHasNew =
    !!openId &&
    (!!openCard?.newActivity || (activity.data?.items || []).some((i) => i.isNew && i.onboardingId === openId));
  const markSeenRef = useRef(markSeen.mutate);
  markSeenRef.current = markSeen.mutate;
  useEffect(() => {
    if (openHasNew) markSeenRef.current({ onboardingId: openId });
  }, [openHasNew, openId]);
  const columns = useMemo(() => data?.columns || {}, [data]);
  const q = query.trim().toLowerCase();
  const matchesQuery = (c) =>
    !q ||
    `${c.name} ${c.email || ''} ${c.phone || ''}`.toLowerCase().includes(q);
  // Who is on the board (for the avatar filter): every owner with a card.
  const owners = useMemo(() => {
    const map = new Map();
    Object.values(columns)
      .flat()
      .forEach((c) => {
        if (!c.ownerUserId) return;
        const o = map.get(c.ownerUserId) || { userId: c.ownerUserId, name: c.ownerName || 'Specialist', count: 0 };
        o.count += 1;
        map.set(c.ownerUserId, o);
      });
    return [...map.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [columns]);
  const [hiddenOwners, setHiddenOwners] = useState(() => new Set());
  const toggleOwner = (id) =>
    setHiddenOwners((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const ownerShown = (c) => !showingAll || !hiddenOwners.has(c.ownerUserId);
  // The board, filtered by the search and the avatars, column by column.
  const shown = Object.fromEntries(
    COLUMNS.map((col) => [
      col.key,
      (columns[col.key] || []).filter((c) => matchesQuery(c) && ownerShown(c)),
    ]),
  );
  const boardTotal = COLUMNS.reduce((n, col) => n + (columns[col.key] || []).length, 0);
  const boardShown = COLUMNS.reduce((n, col) => n + shown[col.key].length, 0);
  // How many hits each tab has for the current search (null: not searching).
  // Nurture's hits come from the server search.
  const hitsBy = q
    ? { board: boardShown, nurture: nurtureQ ? nurture.data?.total || 0 : 0 }
    : null;
  // The "Filtered by" bar (Nic 2026-10-27, like Sales): what this tab shows
  // of what it has. Signed SAs reports its own (server) counts.
  const [signedCounts, setSignedCounts] = useState(null);
  const filterCounts = !q
    ? null
    : section === 'signed'
      ? signedCounts || { shown: null, total: 0 }
      : section === 'nurture'
        ? { shown: nurtureQ && !nurture.isFetching ? nurture.data?.total || 0 : null, total: data?.nurtureTotal ?? 0 }
        : { shown: boardShown, total: boardTotal };

  // ---- Tab motion: a measured pill slides to the selected tab, and the list
  // slides in from the side you clicked. Transforms / opacity only (no
  // layout thrash); off for anyone who prefers reduced motion.
  const tabKeys = SECTIONS.map((x) => x.key);
  const tabListRef = useRef(null);
  const tabRefs = useRef({});
  const contentRef = useRef(null);
  const prevSection = useRef(section);
  const [pill, setPill] = useState(null); // { left, width, animate }
  const pillSection = useRef(null);
  const reduceMotion =
    typeof window !== 'undefined' &&
    window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

  useLayoutEffect(() => {
    // Only a tab click glides the pill. Resizes and data refreshes snap it,
    // so the strip never twitches on its own.
    const measure = () => {
      const el = tabRefs.current[section];
      if (!el) return;
      const animate =
        pillSection.current !== null && pillSection.current !== section;
      pillSection.current = section;
      setPill((p) =>
        p && p.left === el.offsetLeft && p.width === el.offsetWidth
          ? p
          : { left: el.offsetLeft, width: el.offsetWidth, animate },
      );
    };
    measure();
    // Counts and the Everyone label change tab widths; so does the window.
    const ro =
      typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(measure)
        : null;
    if (ro && tabListRef.current) ro.observe(tabListRef.current);
    window.addEventListener('resize', measure);
    return () => {
      ro?.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [section, data?.canManage, board.dataUpdatedAt]);

  useLayoutEffect(() => {
    const from = prevSection.current;
    prevSection.current = section;
    const node = contentRef.current;
    if (!node || from === section || reduceMotion) return;
    const dir = tabKeys.indexOf(section) > tabKeys.indexOf(from) ? 1 : -1;
    node.style.transition = 'none';
    node.style.transform = `translateX(${dir * 28}px)`;
    node.style.opacity = '0';
    // Next frame: glide in.
    const raf = requestAnimationFrame(() => {
      node.style.transition =
        'transform 240ms cubic-bezier(0.22, 1, 0.36, 1), opacity 200ms ease-out';
      node.style.transform = 'translateX(0)';
      node.style.opacity = '1';
    });
    // Drop the transform once settled: a lingering transform makes a new
    // containing block for anything fixed inside the list.
    const done = setTimeout(() => {
      node.style.transition = '';
      node.style.transform = '';
      node.style.opacity = '';
    }, 300);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(done);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [section]);


  return (
    <PrivateLayout background={theme === 'dark' ? '#0E121B' : '#F5F7FA'} hideFooter fluid>
      {/* The whole window, like the Sales board (Nic 2026-10-10). The top
          stays put and only the content scrolls, in its own box (Nic
          2026-10-23, like Sales). */}
      <div className="flex h-[calc(100vh-125px)] min-h-0 w-full flex-col">
        {/* The top, like the Sales board (Nic 2026-10-10): two tight rows.
            The title on the left; the people, RingCentral and page tools on
            the right; then the tabs, with search and the theme switch. */}
        <div className="mb-2.5 flex shrink-0 flex-wrap items-center gap-x-3 gap-y-2 border-b border-slate-200/80 pb-2.5 dark:border-white/10">
          <div className="min-w-0">
            <h2 className="mb-0 text-[19px] font-bold leading-tight tracking-[-0.01em] text-slate-800 dark:text-slate-100">
              Onboarding Workspace
            </h2>
          </div>
          <div className="ml-auto flex flex-wrap items-center justify-end gap-2">
            {/* Who has whom (Nic 2026-10-05, like Sales): a circle per
                specialist with a card on the board; click to hide or show
                their clients. Only while everyone's board is showing. */}
            {showingAll && section === 'board' && owners.length ? (
              <div className="flex items-center gap-2">
                {owners.map((o) => {
                  const off = hiddenOwners.has(o.userId);
                  const initials = o.name
                    .split(/\s+/)
                    .filter(Boolean)
                    .slice(0, 2)
                    .map((w) => w[0].toUpperCase())
                    .join('');
                  return (
                    <button
                      key={o.userId}
                      type="button"
                      onClick={() => toggleOwner(o.userId)}
                      aria-pressed={!off}
                      title={`${o.name}: ${o.count} client${o.count === 1 ? '' : 's'}\n${
                        off ? 'Hidden. Click to show their clients again' : 'Click to hide their clients'
                      }`}
                      className="relative inline-flex rounded-full outline-none focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/60"
                    >
                      <span
                        className={cn(
                          'flex h-8 w-8 items-center justify-center rounded-full bg-slate-800 text-[11px] font-bold text-white ring-2 ring-white transition-opacity dark:bg-slate-200 dark:!text-slate-900 dark:ring-surface-page',
                          off && 'opacity-30',
                        )}
                      >
                        {initials}
                      </span>
                      <span
                        className={cn(
                          'absolute -bottom-1 -right-1 flex min-w-[18px] items-center justify-center rounded-full bg-white px-1 text-[10px] font-bold tabular-nums text-slate-600 ring-1 ring-slate-200 dark:!bg-surface-elevated dark:text-slate-300 dark:ring-white/10',
                          off && 'opacity-30',
                        )}
                      >
                        {o.count}
                      </span>
                    </button>
                  );
                })}
              </div>
            ) : null}
            {/* RingCentral and My Calendly in one button, like Sales
                (Nic 2026-10-21: was two pills). Only for the team and its
                manager (Nic 2026-11-02, the same role check as acting on
                people): an admin or viewer has nothing to call or text
                from here. */}
            {data?.viewer?.canAct === true ? (
              <ConnectionsControl
                myCalendlyUrl={data?.myCalendlyUrl || ''}
                // Calendly is gone (in-app meetings, 2026-10-25): RingCentral only.
                withCalendly={false}
                apiBase="/client-onboarding"
              />
            ) : null}
            {/* View only (Nic 2026-10-05): watch, never act. */}
            {viewOnly ? (
              <span className="inline-flex h-8 items-center gap-1.5 whitespace-nowrap rounded-full bg-slate-900/[0.06] px-3.5 text-[12px] font-semibold text-slate-600 dark:!bg-white/10 dark:text-slate-300">
                <Eye size={14} /> View only
              </span>
            ) : null}
            <EagleEyeButton />
            {data?.canManage ? <TeamSettingsButton team="onboarding" /> : null}
          </div>
        </div>

        <div className="mb-2.5 flex shrink-0 flex-wrap items-center gap-2">
          <div
            role="tablist"
            // The Sales board's tab look (Nic 2026-09-28): one rounded track,
            // the selected tab a white pill that slides between tabs.
            ref={tabListRef}
            // Scrolls when narrow, with no visible bar (it drew over the curve).
            className="relative flex max-w-full gap-0.5 overflow-x-auto rounded-full bg-slate-200/70 p-1 [scrollbar-width:none] dark:!bg-white/10 [&::-webkit-scrollbar]:hidden"
          >
            {pill ? (
              <span
                aria-hidden
                className="absolute inset-y-1 rounded-full bg-white shadow-sm transition-[left,width] duration-300 ease-out motion-reduce:transition-none dark:!bg-surface-elevated"
                style={{
                  left: pill.left,
                  width: pill.width,
                  transition: pill.animate ? undefined : 'none',
                }}
              />
            ) : null}
            {/* No Scorecard tab (Nic 2026-10-05): the team numbers live on
                Lead Metrics' Onboarding Team tab. */}
            {SECTIONS.map((s) => {
              // A number only when something needs YOU (Nic 2026-10-23, like
              // Sales): Clients counts your own clients with something for
              // you today; Stalled and Signed SAs never need you. Admins
              // and viewers, who work nothing, see none. A search hit still
              // shows its tab's count.
              const needsYou =
                s.key === 'board' && data?.viewer?.canAct !== false
                  ? clientsNeedingYou(columns, user?.userId, tick, data?.stallDays || 14)
                  : 0;
              const searchCount =
                s.key === 'signed' ? null : s.key === 'nurture' ? data?.nurtureTotal ?? null : boardTotal;
              const count = needsYou > 0 ? needsYou : searchCount;
              const on = section === s.key;
              // A search hit on this tab: its number gets a blue ring, so a
              // match on another tab is never missed.
              const hit = !!hitsBy && (hitsBy[s.key] || 0) > 0;
              return (
                <button
                  key={s.key}
                  ref={(el) => {
                    tabRefs.current[s.key] = el;
                  }}
                  type="button"
                  role="tab"
                  aria-selected={on}
                  onClick={() => setSection(s.key)}
                  className={cn(
                    'relative z-10 inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-full px-4 py-1.5 text-[13px] font-semibold outline-none transition-[color] focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/60',
                    on
                      ? 'text-slate-900 dark:text-white'
                      : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200',
                  )}
                >
                  {s.label}
                  {count == null ? null : needsYou > 0 ? (
                    // Something needs you: red like Sales, the halo pulsing
                    // while you're on another tab.
                    <span className="relative inline-flex">
                      {!on ? (
                        <span
                          aria-hidden
                          className="absolute inset-0 animate-ping rounded-full bg-red-500 opacity-60 motion-reduce:hidden"
                        />
                      ) : null}
                      {hit ? (
                        <span
                          aria-hidden
                          className="absolute inset-0 animate-ping-soft rounded-full shadow-[0_0_0_1.5px_rgba(14,165,233,0.9)] motion-reduce:hidden"
                        />
                      ) : null}
                      <span
                        className={cn(
                          'relative min-w-[1.25rem] rounded-full bg-red-500 px-1.5 text-center text-[11px] tabular-nums text-white',
                          hit && 'shadow-[0_0_0_1.5px_rgba(14,165,233,0.9)]',
                        )}
                      >
                        {count}
                      </span>
                    </span>
                  ) : hit ? (
                    // A plain total is no number at all (Nic 2026-10-23: a
                    // tab shows a number only when something needs you); a
                    // search hit still shows its count.
                    <span className="relative inline-flex">
                      {/* A search hit: the blue outline pulses outward. */}
                      {hit ? (
                        <span
                          aria-hidden
                          className="absolute inset-0 animate-ping-soft rounded-full shadow-[0_0_0_1.5px_rgba(14,165,233,0.9)] motion-reduce:hidden"
                        />
                      ) : null}
                      <span
                        className={cn(
                          'relative min-w-[1.25rem] rounded-full px-1.5 text-center text-[11px] tabular-nums transition-[box-shadow,background-color,color]',
                          hit
                            ? 'bg-sky-50 text-sky-700 shadow-[0_0_0_1.5px_rgba(14,165,233,0.85),0_0_8px_rgba(14,165,233,0.3)] dark:!bg-sky-500/15 dark:text-sky-200'
                            : 'bg-slate-900/[0.06] text-slate-500 dark:!bg-white/10 dark:text-slate-400',
                        )}
                      >
                        {count}
                      </span>
                    </span>
                  ) : null}
                </button>
              );
            })}
          </div>
          {/* How Clients works (Nic 2026-10-29): the desk's rules as a
              right-hand panel, like Sales' Signing Up. Clients tab only. */}
          {section === 'board' ? <ClientsGuide /> : null}
          <div className="ml-auto flex w-full items-center gap-2 sm:w-auto">
            <label className="relative block min-w-0 flex-1 sm:w-64 sm:flex-none">
              <Search
                size={14}
                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
              />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search name, email, phone"
                className={cn(
                  'h-8 w-full rounded-full bg-white pl-8 pr-9 text-[13px] text-slate-700 outline-none dark:!bg-white/5 dark:text-slate-200',
                  // Filtering: a steady blue outline so it is never forgotten.
                  query.trim()
                    ? 'shadow-[0_0_0_1.5px_rgba(14,165,233,0.85)]'
                    : 'ring-1 ring-slate-200 focus:ring-slate-300 dark:ring-white/10',
                )}
              />
              {query ? (
                <button
                  type="button"
                  onClick={() => setQuery('')}
                  aria-label="Clear search"
                  title="Clear search"
                  className="absolute right-1.5 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full text-sky-600 outline-none hover:bg-sky-50 focus:outline-none dark:text-sky-300 dark:hover:!bg-sky-500/15"
                >
                  <BrushCleaning size={14} />
                </button>
              ) : null}
            </label>
            {/* Light or dark (Nic 2026-10-11: in place of Going to Stalled
                tonight and the settings button). */}
            {/* What clients did (Nic 2026-10-23): a count and a pulse here,
                never a pile of toasts. */}
            {/* The scoreboard chip (Nic 2026-11-03): the specialist's own
                FOTCs today, NEW until opened. Only for the people who own
                clients: for an admin or view only it would always read 0. */}
            {data?.viewer?.canAct === true ? <ScoreboardChip team="onboarding" /> : null}
            <ClientActivityButton
              viewOnly={viewOnly}
              labels={checklistLabels}
              now={tick}
              onOpenClient={openClientAt}
            />
            <button
              type="button"
              onClick={() => setThemeTo(theme === 'dark' ? 'light' : 'dark')}
              title={theme === 'dark' ? 'Light theme' : 'Dark theme'}
              aria-label={theme === 'dark' ? 'Switch to the light theme' : 'Switch to the dark theme'}
              className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white text-slate-500 shadow-[0_1px_2px_rgba(15,23,42,0.06)] outline-none ring-1 ring-slate-200/80 hover:bg-slate-50 hover:text-slate-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/60 dark:!bg-white/5 dark:text-slate-300 dark:ring-white/10 dark:hover:!bg-white/10 dark:hover:text-white"
            >
              {theme === 'dark' ? <Sun size={15} strokeWidth={2.25} /> : <Moon size={15} strokeWidth={2.25} />}
            </button>
            {/* No Meetings button (Nic 2026-10-11): the Schedule tile's Calendar
                opens the same calendar. */}
          </div>
        </div>

        {/* The same hairline under the tabs as Sales. */}
        <div className="mb-2.5 h-px w-full shrink-0 bg-slate-300/60 dark:!bg-white/15" />

        {filterCounts ? (
          <SearchFilterBar q={query.trim()} shown={filterCounts.shown} total={filterCounts.total} onClear={() => setQuery('')} />
        ) : null}

        {/* Only this scrolls; overflow-hidden clips the tab motion's nudge,
            and a little room keeps the tiles off the scrollbar. */}
        <div className="relative min-h-0 flex-1 overflow-hidden">
          {/* p-0.5 like Sales: room for a selected tile's or a filtered list's outline. */}
          <div ref={contentRef} className="os-scroll h-full min-h-0 overflow-y-auto p-0.5 pb-4 pr-2">
          {board.isLoading ? (
            <div className="flex h-48 items-center justify-center">
              <Loader2 size={20} className="animate-spin text-slate-400" />
            </div>
          ) : board.isError ? (
            <div
              className={cn(
                CARD,
                'flex items-center gap-2 p-5 text-sm text-rose-600 dark:text-rose-300',
              )}
            >
              <AlertTriangle size={16} />
              {board.error?.response?.data?.msg ||
                "The Onboarding Workspace couldn't be loaded."}
              <button
                type="button"
                onClick={() => board.refetch()}
                className="ml-auto inline-flex items-center gap-1 whitespace-nowrap text-xs font-semibold text-slate-500 hover:text-slate-800 dark:hover:text-white"
              >
                <RotateCcw size={13} /> Try again
              </button>
            </div>
          ) : section === 'signed' ? (
            <SignedSasView q={nurtureQ} onCounts={setSignedCounts} />
          ) : section === 'nurture' ? (
            // Stalled looks and filters like the Sales board's Nurture
            // (Nic 2026-10-10). The header search drives it too.
            <StalledView q={nurtureQ} canReengage={!!data?.canReengage} viewer={data?.viewer || null} onOpen={setOpenId} />
          ) : (
            <>
              {/* One desk (Nic 2026-10-11): the counters, every client on a
                  row, Today's to-dos and the schedule. */}
              <OnboardingDesk
                shown={shown}
                now={tick}
                viewerId={user?.userId}
                showOwner={showingAll}
                canAct={!viewOnly}
                labels={checklistLabels}
                stallDays={data?.stallDays || 14}
                workCalendar={data?.workCalendar || null}
                pinned={data?.pinned}
                hideDone={!!data?.prefs?.hideDone}
                hideReady={!!data?.prefs?.hideReady}
                scoreboardFolded={!!data?.prefs?.scoreboardFolded}
                searching={!!q}
                onOpenNotes={(id) => openClientAt(id, 'notes')}
                onNudge={viewOnly ? null : (c) => setNudge({ onboardingId: c.onboardingId, name: c.name })}
                meetingScope={showingAll ? 'all' : 'mine'}
                onOpen={setOpenId}
                onOpenCalendar={() => setMeetingsOpen(true)}
                // Moved to CM today (Nic 2026-10-26): assigned today.
                assignedToCm={(data?.assignedToCm || []).filter((c) => matchesQuery(c) && ownerShown(c))}
              />
            </>
          )}
          </div>
        </div>
      </div>

      <MeetingsCalendar
        open={meetingsOpen}
        onClose={() => setMeetingsOpen(false)}
        now={tick}
        // Specialists see their own meetings (no Everyone toggle, Nic 2026-10-11);
        // managers, admins and view only see everyone.
        seesAll={seesAll}
        viewerUserId={user?.userId}
        apiBase="/client-onboarding"
        onOpen={(m) => {
          if (!m?.onboardingId) return;
          setMeetingsOpen(false);
          setOpenId(m.onboardingId);
        }}
        onCall={(m) => callClient(m)}
        canCall={meetingIsMine}
      />
      <NudgeComposer
        open={!!nudge}
        draftKey={nudge?.onboardingId}
        name={nudge?.name}
        loadDraft={() => fetchOnboardingTextDraft(nudge.onboardingId)}
        sendText={({ text, rcAccessToken }) => sendText.mutateAsync({ onboardingId: nudge.onboardingId, text, rcAccessToken })}
        sending={sendText.isLoading}
        onClose={() => setNudge(null)}
      />
      {/* The Client Workspace for everyone (Nic 2026-10-09; viewers and
          admins too since 2026-10-23, read only: no more slide-over). */}
      {openId ? (
        <ClientWorkspace
          onboardingId={openId}
          canManage={!!data?.canManage}
          initialTab={openAt?.id === openId ? openAt.tab : null}
          canReengage={!!data?.canReengage}
          viewer={data?.viewer || null}
          onClose={() => {
            setOpenId(null);
            setOpenAt(null);
          }}
        />
      ) : null}
    </PrivateLayout>
  );
};

export default ClientOnboardingPage;
